import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { listApplications } from '../api';
import { FileText, Plus, ArrowRight, TrendingUp, CheckCircle2, Clock, User, Building2, Globe, Calculator, MessageSquare } from 'lucide-react';

const Dashboard = () => {
  const { t, dir, lang } = useLang();
  const { user } = useAuth();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listApplications().then((d) => setApps(d || [])).catch(() => setApps([])).finally(() => setLoading(false));
  }, []);

  const stats = {
    total: apps.length,
    inProgress: apps.filter((a) => ['in_review', 'documents_pending'].includes(a.status)).length,
    completed: apps.filter((a) => a.status === 'approved').length
  };

  const recent = apps.slice(0, 3);

  return (
    <div className="pt-28 pb-16 min-h-screen" dir={dir}>
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="flex items-center gap-4 mb-10">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#2563eb] to-[#1d4ed8] flex items-center justify-center text-[#f8fafc] font-display text-2xl overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : (user?.name?.charAt(0)?.toUpperCase() || 'U')}
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-[#2563eb]">{t('auth.welcome_back')}</div>
            <h1 className="font-display text-3xl md:text-4xl text-[#0a0a0a]">{user?.name || user?.email}</h1>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          {[
            { icon: FileText, label: t('auth.total_apps'), value: stats.total, color: 'text-[#3b82f6]', bg: 'bg-[#2563eb]/10 border-[#2563eb]/25' },
            { icon: Clock, label: t('auth.in_progress'), value: stats.inProgress, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-400/25' },
            { icon: CheckCircle2, label: t('auth.completed'), value: stats.completed, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-400/25' }
          ].map((s, i) => (
            <div key={i} className="glass-card rounded-2xl p-6 flex items-center gap-4">
              <div className={`w-12 h-12 rounded-lg ${s.bg} border flex items-center justify-center`}>
                <s.icon className={`w-5 h-5 ${s.color}`} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[#111827]/50">{s.label}</div>
                <div className="font-display text-3xl text-[#0a0a0a] mt-1">{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Recent Apps */}
          <div className="md:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl text-[#0a0a0a]">{t('auth.recent_apps')}</h2>
              <Link to="/MyApplications" className="text-sm text-[#3b82f6] hover:text-[#0a0a0a] flex items-center gap-1">
                {t('auth.view_all')} <ArrowRight className="w-3.5 h-3.5 rtl:hidden" />
              </Link>
            </div>
            {loading ? (
              <div className="glass-card rounded-2xl p-10 text-center text-[#111827]/60">…</div>
            ) : recent.length === 0 ? (
              <div className="glass-card rounded-2xl p-10 text-center">
                <FileText className="w-10 h-10 text-[#2563eb]/40 mx-auto mb-4" />
                <p className="text-[#111827]/60 mb-4">{lang === 'ar' ? 'لا توجد طلبات بعد' : 'No applications yet'}</p>
                <Link to="/SetupWizard"><Button className="gold-button rounded-md h-10 px-5 text-xs">{t('auth.new_app')}</Button></Link>
              </div>
            ) : (
              <div className="space-y-3">
                {recent.map((a) => {
                  const TypeIcon = a.type === 'mainland' ? Building2 : Globe;
                  return (
                    <div key={a.id} className="glass-card rounded-xl p-5 flex items-center gap-4">
                      <div className="w-11 h-11 rounded-lg bg-[#2563eb]/10 border border-[#2563eb]/25 flex items-center justify-center flex-shrink-0">
                        <TypeIcon className="w-5 h-5 text-[#3b82f6]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display text-lg text-[#0a0a0a] truncate">{a.company_name}</div>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-xs text-[#2563eb] font-mono">{a.id}</span>
                          <span className="text-xs text-[#111827]/50">{a.zone_name}</span>
                        </div>
                      </div>
                      <div className="text-end min-w-[100px] hidden sm:block">
                        <div className="text-xs text-[#111827]/50">{t('apps.status.' + a.status)}</div>
                        <Progress value={a.progress || 15} className="h-1 bg-[#2563eb]/10 mt-1 w-24" />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div>
            <h2 className="font-display text-2xl text-[#0a0a0a] mb-4">{t('auth.quick_actions')}</h2>
            <div className="space-y-3">
              <Link to="/SetupWizard" className="glass-card rounded-xl p-5 flex items-center gap-3 hover:-translate-y-0.5 transition-transform group">
                <div className="w-10 h-10 rounded-lg bg-[#2563eb]/15 border border-[#2563eb]/30 flex items-center justify-center flex-shrink-0">
                  <Plus className="w-4 h-4 text-[#3b82f6]" />
                </div>
                <div className="flex-1"><div className="text-sm text-[#0a0a0a]">{t('auth.new_app')}</div></div>
                <ArrowRight className="w-4 h-4 text-[#2563eb] rtl:hidden group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/Calculator" className="glass-card rounded-xl p-5 flex items-center gap-3 hover:-translate-y-0.5 transition-transform group">
                <div className="w-10 h-10 rounded-lg bg-[#2563eb]/15 border border-[#2563eb]/30 flex items-center justify-center flex-shrink-0">
                  <Calculator className="w-4 h-4 text-[#3b82f6]" />
                </div>
                <div className="flex-1"><div className="text-sm text-[#0a0a0a]">{t('nav.calculator')}</div></div>
                <ArrowRight className="w-4 h-4 text-[#2563eb] rtl:hidden group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/Comparison" className="glass-card rounded-xl p-5 flex items-center gap-3 hover:-translate-y-0.5 transition-transform group">
                <div className="w-10 h-10 rounded-lg bg-[#2563eb]/15 border border-[#2563eb]/30 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-4 h-4 text-[#3b82f6]" />
                </div>
                <div className="flex-1"><div className="text-sm text-[#0a0a0a]">{t('auth.explore')}</div></div>
                <ArrowRight className="w-4 h-4 text-[#2563eb] rtl:hidden group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/Contact" className="glass-card rounded-xl p-5 flex items-center gap-3 hover:-translate-y-0.5 transition-transform group">
                <div className="w-10 h-10 rounded-lg bg-blue-500/15 border border-blue-400/30 flex items-center justify-center flex-shrink-0">
                  <MessageSquare className="w-4 h-4 text-blue-400" />
                </div>
                <div className="flex-1"><div className="text-sm text-[#0a0a0a]">{t('auth.book_consultation')}</div></div>
                <ArrowRight className="w-4 h-4 text-blue-400 rtl:hidden group-hover:translate-x-0.5 transition-transform" />
              </Link>
              <Link to="/Profile" className="glass-card rounded-xl p-5 flex items-center gap-3 hover:-translate-y-0.5 transition-transform group">
                <div className="w-10 h-10 rounded-lg bg-[#2563eb]/15 border border-[#2563eb]/30 flex items-center justify-center flex-shrink-0">
                  <User className="w-4 h-4 text-[#3b82f6]" />
                </div>
                <div className="flex-1"><div className="text-sm text-[#0a0a0a]">{t('auth.profile')}</div></div>
                <ArrowRight className="w-4 h-4 text-[#2563eb] rtl:hidden group-hover:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
